import React from 'react';
import { useGetOrganizationProfile } from '@features/settings/ordering/hooks/useGetOrganizationProfile';
import type { CSS } from '@goodfynd/theme';
import useOrganizationStripeStatus from '@hooks/useOrganizationStripeStatus';

import ConnectStripe from './ConnectStripe';
import OnboardStripe from './OnboardStripe';
import PayoutsPaused from './PayoutsPaused';
import SetTaxSettings from './SetTaxSettings';

type OrderingStatusAlertOptions = {
  compact?: boolean;
  css?: CSS;
  ctaText?: string;
  organizationId: string;
  text?: string;
};

export default function OrderingStatusAlert({
  compact,
  css,
  ctaText,
  organizationId,
  text,
}: OrderingStatusAlertOptions) {
  const { status, isFetched } = useOrganizationStripeStatus({ organizationId });
  const { organization, isFetched: orgFetched } = useGetOrganizationProfile({
    id: organizationId,
  });

  if (orgFetched && organization?.stripeAccountPayoutsPaused === true) {
    const buttonClick = () => {
      window.open(`https://cal.com/team/goodfynd/cx?duration=15`, '_blank');
    };
    return (
      <PayoutsPaused
        buttonClick={buttonClick}
        compact={compact}
        ctaText="👉 Schedule Your Call"
        icon="info"
        text="Stripe is set up! A quick call is needed to finalize your payout schedule and learn more about your business so we can best support you with Goodfynd."
        type="warning"
      />
    );
  }

  if (
    !isFetched ||
    (status?.accountId &&
      status.chargesEnabled &&
      status.detailsSubmitted &&
      status.payoutsEnabled &&
      (status.taxSettingsSubmitted == undefined || status.taxSettingsSubmitted))
  ) {
    return null;
  }

  if (
    !isFetched ||
    (status?.accountId &&
      status.chargesEnabled &&
      status.detailsSubmitted &&
      status.payoutsEnabled &&
      status.taxSettingsSubmitted !== undefined &&
      !status.taxSettingsSubmitted)
  ) {
    return (
      <SetTaxSettings
        compact={compact}
        text="Before you take your first order, make sure your business profile is complete."
      />
    );
  }

  if (
    status?.accountId &&
    (!status?.chargesEnabled ||
      !status?.detailsSubmitted ||
      !status?.payoutsEnabled)
  ) {
    return <OnboardStripe compact={compact} ctaText={ctaText} text={text} />;
  }

  return (
    <ConnectStripe compact={compact} css={css} ctaText={ctaText} text={text} />
  );
}
